import React, { useState } from 'react';
import Breadcrumbs from '../common/Breadcrumbs';
import Row from '../common/Row';
import TimeStamp from '../common/TimeStamp';
import Icon from '../common/Icon';
import FieldEdit from './FieldEdit';
import * as f from './constants/subtypes';
import { FieldContext, MetadataEntryContext } from '../../hooks/Contexts';
import OptionsIndex from '../options/OptionsIndex';
import MetadataEntries from '../metadata/MetadataEntries';
import { handleDestroyField } from './constants/handleField';
import { useLoaderData, useNavigate } from 'react-router-dom';

const FieldShow = () => {
  const { data } = useLoaderData();
  const navigate = useNavigate();
  const { container, form, fieldset, informant, options, meta_entries } = data;
  const isAdminField = !fieldset.public;
  const [editing, setEditing] = useState(false);
  const [metaEntries, setMetaEntries] = useState(meta_entries);
  const [field, setField] = useState(data.field);

  const toggleEdit = () => setEditing(!editing);

  const {
    char_limit,
    char_min,
    created_at,
    default_value,
    display_name,
    file_ext_whitelist,
    help_text,
    informs_global,
    name,
    placeholder,
    quick_edit,
    required,
    type,
    universal_quick_edit,
    unread_quick_edit,
    updated_at,
    validator,
    width,
  } = field;

  const handleDestroy = async () => {
    await handleDestroyField(field.id, (response) => {
      if (response.ok) {
        navigate(`/manage/fieldsets/${fieldset.id}`);
      }
    });
  };

  return (
    <>
      <Breadcrumbs container={container} form={form} fieldset={fieldset} />
      {editing ? (
        <FieldEdit
          field={field}
          toggleEdit={toggleEdit}
          informant={informant}
          setField={setField}
        />
      ) : (
        <>
          <div className='details'>
            <div className='controls'>
              <Icon className='edit' onClick={toggleEdit} icon='pencil' />
              <Icon className='destroy' onClick={handleDestroy} icon='trash' />
            </div>
            <Row name='Display Name' value={display_name} />
            <Row name='Name' value={name} />
            <Row name='Help Text' value={help_text} />
            <Row name='Type' value={type} />
            {f.allowQuickEdit(type) && (
              <>
                <Row
                  name='Quick Edit Allowed'
                  value={quick_edit ? 'Yes' : 'No'}
                />
                {quick_edit && (
                  <>
                    <Row
                      name='Allow Quick Edit for All Roles'
                      value={universal_quick_edit ? 'Yes' : 'No'}
                    />

                    <Row
                      name='Allow Quick Edit for Unread Submissions'
                      value={unread_quick_edit ? 'Yes' : 'No'}
                    />
                  </>
                )}
              </>
            )}
            {f.isAllowedQuota(type) && (
              <Row
                name='Informs Global'
                value={informs_global ? 'Yes' : 'No'}
              />
            )}
            <Row name='Required' value={required ? 'Yes' : 'No'} />
            {f.canChangeWidth(type) && (
              <Row name='Width' value={f.widthName(width)} />
            )}

            {f.isFileType(type) && (
              <Row
                name='Active'
                value={file_ext_whitelist ? file_ext_whitelist : 'all'}
              />
            )}
            {f.isOptionType(type) && <Row name='Validator' value={validator} />}
            {f.isTextType(type) && (
              <>
                <Row name='Validator' value={validator} />
                {f.validatorLimits(type) && (
                  <>
                    <Row name='Character minimum' value={char_min} />
                    <Row name='Character limit' value={char_limit} />
                  </>
                )}
              </>
            )}
            {f.isPlaceholderType(type) && (
              <Row name='Placeholder' value={placeholder} />
            )}
            {f.isTextType(type) && (
              <Row name='Default Value' value={default_value} />
            )}
            <TimeStamp title='Created' value={created_at} />
            <TimeStamp title='Updated' value={updated_at} />
          </div>
        </>
      )}

      {f.isOptionType(type) && (
        <FieldContext.Provider value={{ field, setField }}>
          {field.type != 'checkbox' && (
            <MetadataEntries
              metaEntries={metaEntries}
              setMetaEntries={setMetaEntries}
            />
          )}
          <MetadataEntryContext.Provider value={metaEntries}>
            <OptionsIndex allOptions={options} isAdminField={isAdminField} />
          </MetadataEntryContext.Provider>
        </FieldContext.Provider>
      )}
    </>
  );
};

export default FieldShow;
